import { usePageTitle } from '@/hooks/usePageTitle'
import { VacancyListPage } from '@/features/profile/VacancyListPage'

/**
 * Port of Views/Profile/ApplicationHistory.cshtml +
 * wwwroot/js/app/profile/applicationHistory.js.
 *
 * Same screen as Saved Vacancy with two differences: a Status line (Open/Close,
 * from the deadline) in the card body, and a footer that shows where the
 * application has got to instead of an Apply button. No bookmark here.
 */
export function ApplicationHistory(): JSX.Element {
  usePageTitle('Application History')

  return (
    <VacancyListPage
      listUrl="/api/ecareer/vacancy/listApplicationHistory"
      careerUrl="/api/ecareer/career/listApplicationHistory"
      companyUrl="/api/ecareer/company/listApplicationHistory"
      positionUrl="/api/ecareer/position/listApplicationHistory"
      renderExtraBody={(vacancy) => {
        const isExpired = hasClosed(vacancy.EndVacancy)
        return (
          <p className="card-text text-muted text-sm">
            <i className="ti ti-info-circle text-primary" /> Status :{' '}
            <span className={`fw-bold ${isExpired ? 'text-danger' : 'text-success'}`}>
              {isExpired ? 'Close' : 'Open'}
            </span>
          </p>
        )
      }}
      renderFooter={(vacancy) => {
        const status = applicationStatusButton(vacancy.VacancyStepName)
        return (
          <>
            <span className="text-muted me-3">Application status:</span>
            <button
              type="button"
              className={`${status.className} btnApplyNow`}
              data-vacancy-id={vacancy.trxVacancyID}
              disabled={status.disabled}
              style={
                status.disabled
                  ? { cursor: 'not-allowed', opacity: 0.8 }
                  : { cursor: 'pointer' }
              }
            >
              {status.text}
            </button>
          </>
        )
      }}
    />
  )
}

/** A missing or unparseable deadline counts as still open, as in the original. */
function hasClosed(endVacancy: string | null | undefined): boolean {
  if (!endVacancy) return false
  const end = new Date(endVacancy)
  if (Number.isNaN(end.getTime())) return false
  return new Date() > end
}

/**
 * Port of `ApplicationHistory.GetApplicationStatusButton`.
 *
 * Every branch produces a disabled button — including the default, which shows
 * the raw step name. Only a null/empty step falls through to an enabled
 * "Apply Now", which is the state of a row that has no application step yet.
 */
function applicationStatusButton(vacancyStepName: string | null | undefined): {
  className: string
  text: string
  disabled: boolean
} {
  if (!vacancyStepName) {
    return { className: 'btn btn-primary', text: 'Apply Now', disabled: false }
  }

  switch (vacancyStepName.toLowerCase()) {
    case 'submitted':
      return { className: 'btn btn-info', text: 'Submitted', disabled: true }
    case 'in review':
    case 'review':
      return { className: 'btn btn-warning', text: 'In Review', disabled: true }
    case 'interview':
    case 'scheduled':
      return { className: 'btn btn-secondary', text: 'Interview Scheduled', disabled: true }
    case 'accepted':
    case 'hired':
      return { className: 'btn btn-success', text: 'Accepted', disabled: true }
    case 'rejected':
    case 'declined':
      return { className: 'btn btn-danger', text: 'Rejected', disabled: true }
    default:
      return { className: 'btn btn-secondary', text: vacancyStepName, disabled: true }
  }
}
